/**
 * MASTER — MACRO / «Макро-кадр».
 *
 * One dominant editorial image with one short thought. A visual pause —
 * NOT sensory analysis. No cards, rounded corners, shadows, sensory notes,
 * Q&A, tables, or multiple text sections. Reusable across every Digital
 * Editions surface — nothing here is Almond/product/material/route specific.
 *
 * Storage:
 *   `block.content.macro` (schema exposes `macro: Optional[Dict[str, Any]]`).
 *   Legacy top-level keys of `block.content` are preserved verbatim.
 *
 * Shared primitives reused from HeroMaster / CropModal.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import CropModal from "@/components/editorial/CropModal";
import { ImageSlot, MediaLibraryPicker } from "@/components/editorial/HeroMaster";

function readMacro(rawContent) {
  const c = rawContent || {};
  const m = (c.macro && typeof c.macro === "object") ? c.macro : {};

  const readImg = (img) => ({
    media_id: img?.media_id ?? null,
    url:      img?.url      ?? null,
    alt:      img?.alt      ?? "",
    focal_x:  img?.focal_x  ?? 0.5,
    focal_y:  img?.focal_y  ?? 0.5,
    zoom:     img?.zoom     ?? 1,
  });

  return {
    eyebrow:   m.eyebrow   ?? "",
    headline:  m.headline  ?? c.headline ?? "",
    thought:   m.thought   ?? c.body     ?? "",
    signature: m.signature ?? c.signature ?? "",
    caption:   m.caption   ?? c.caption   ?? "",
    main_image:   readImg(m.main_image),
    mobile_image: readImg(m.mobile_image),
  };
}

// -------------------- Editor --------------------

export function MacroEditor({ block, onPatch }) {
  const m = useMemo(() => readMacro(block.content), [block.content]);
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), macro: { ...m, ...delta } },
  });

  const [mainLib, setMainLib] = useState(false);
  const [mobileLib, setMobileLib] = useState(false);
  const [mainCrop, setMainCrop] = useState(false);
  const [mobileCrop, setMobileCrop] = useState(false);

  const setMain   = (delta) => patch({ main_image:   { ...m.main_image,   ...delta } });
  const setMobile = (delta) => patch({ mobile_image: { ...m.mobile_image, ...delta } });

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="macro-editor">
      {/* LEFT — text */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Макро-кадр · текст</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Метка раздела · необязательно</label>
          <Input value={m.eyebrow}
                 onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="МАКРО · ПАУЗА"
                 data-testid="macro-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Короткий заголовок · необязательно</label>
          <Input value={m.headline}
                 onChange={(e) => patch({ headline: e.target.value })}
                 className="font-serif text-lg"
                 placeholder="Одна тихая фраза"
                 data-testid="macro-field-headline" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Короткая мысль</label>
          <textarea rows={3} value={m.thought}
                    onChange={(e) => patch({ thought: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif leading-relaxed"
                    placeholder="Одна-две коротких фразы, которые остаются на кадре"
                    data-testid="macro-field-thought" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подпись · источник · необязательно</label>
          <Input value={m.signature}
                 onChange={(e) => patch({ signature: e.target.value })}
                 placeholder="Автор, книга, год"
                 data-testid="macro-field-signature" />
        </div>
      </div>

      {/* RIGHT — images */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Макро-изображение</div>

        <div>
          <div className="text-[11px] opacity-60 mb-2">Основное макро · desktop</div>
          <ImageSlot
            label=""
            url={m.main_image.url}
            focalX={m.main_image.focal_x} focalY={m.main_image.focal_y} zoom={m.main_image.zoom}
            onFile={(x) => setMain({ media_id: x.id, url: x.file_url })}
            onClear={() => setMain({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMainLib(true)}
            onOpenCrop={() => setMainCrop(true)}
            testid="macro-main-image" />
          <div className="mt-2">
            <Input placeholder="Alt-текст · необязательно"
                   value={m.main_image.alt}
                   onChange={(e) => setMain({ alt: e.target.value })}
                   data-testid="macro-main-alt" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[11px] opacity-60 mb-2">
            Мобильное макро · необязательно
            <span className="ml-1 opacity-70">(если пусто — используется основное)</span>
          </div>
          <ImageSlot
            label=""
            url={m.mobile_image.url}
            focalX={m.mobile_image.focal_x} focalY={m.mobile_image.focal_y} zoom={m.mobile_image.zoom}
            onFile={(x) => setMobile({ media_id: x.id, url: x.file_url })}
            onClear={() => setMobile({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMobileLib(true)}
            onOpenCrop={() => setMobileCrop(true)}
            testid="macro-mobile-image" />
          <div className="mt-2">
            <Input placeholder="Alt-текст · необязательно"
                   value={m.mobile_image.alt}
                   onChange={(e) => setMobile({ alt: e.target.value })}
                   data-testid="macro-mobile-alt" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <label className="block text-[11px] opacity-60 mb-1">Подпись под кадром · необязательно</label>
          <Input value={m.caption}
                 onChange={(e) => patch({ caption: e.target.value })}
                 placeholder="Короткая подпись"
                 data-testid="macro-field-caption" />
        </div>
      </div>

      <MediaLibraryPicker open={mainLib} onClose={() => setMainLib(false)}
        onPick={(x) => { setMain({ media_id: x.id, url: x.file_url || x.url }); setMainLib(false); }} />
      <MediaLibraryPicker open={mobileLib} onClose={() => setMobileLib(false)}
        onPick={(x) => { setMobile({ media_id: x.id, url: x.file_url || x.url }); setMobileLib(false); }} />
      <CropModal open={mainCrop}
        url={m.main_image.url}
        focalX={m.main_image.focal_x} focalY={m.main_image.focal_y} zoom={m.main_image.zoom}
        onClose={() => setMainCrop(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMain({ focal_x, focal_y, zoom }); setMainCrop(false); }} />
      <CropModal open={mobileCrop}
        url={m.mobile_image.url}
        focalX={m.mobile_image.focal_x} focalY={m.mobile_image.focal_y} zoom={m.mobile_image.zoom}
        onClose={() => setMobileCrop(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMobile({ focal_x, focal_y, zoom }); setMobileCrop(false); }} />
    </div>
  );
}

// -------------------- Render --------------------

export function MacroRender({ block, mode = "desktop" }) {
  const m = useMemo(() => readMacro(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const activeImage = isMobile
    ? (m.mobile_image.url ? m.mobile_image : m.main_image)
    : m.main_image;
  const hasImage = !!(activeImage && activeImage.url);
  const fx = (activeImage.focal_x ?? 0.5) * 100;
  const fy = (activeImage.focal_y ?? 0.5) * 100;

  const Eyebrow = m.eyebrow ? (
    <div className="text-[10px] tracking-[0.32em] uppercase"
         style={{ color: "#F4EBD8" }}
         data-testid="macro-eyebrow">
      {m.eyebrow}
    </div>
  ) : null;

  const Headline = m.headline ? (
    <div className="mt-2 font-serif leading-[1.15]"
         style={{
           fontSize: isMobile ? "clamp(22px, 5.6vw, 28px)" : "clamp(28px, 2.6vw, 40px)",
           color: "#FFFDF7",
           letterSpacing: "-0.005em",
         }}
         data-testid="macro-headline">
      {m.headline}
    </div>
  ) : null;

  const Thought = m.thought ? (
    <div className={`${m.eyebrow || m.headline ? "mt-4" : ""} font-serif italic leading-[1.55] max-w-[52ch]`}
         style={{
           fontSize: isMobile ? "16px" : "clamp(17px, 1.4vw, 20px)",
           color: "#FFF7EA",
         }}
         data-testid="macro-thought">
      {m.thought}
    </div>
  ) : null;

  const Signature = m.signature ? (
    <div className="mt-4 text-[11px] tracking-[0.24em] uppercase"
         style={{ color: "#E9D6B0" }}
         data-testid="macro-signature">
      — {m.signature}
    </div>
  ) : null;

  const Caption = m.caption ? (
    <div className="mt-3 text-[12px] italic font-serif leading-[1.5] max-w-[42ch]"
         style={{ color: "#5B4326" }}
         data-testid="macro-caption">
      {m.caption}
    </div>
  ) : null;

  // -------- No image → quiet text-only spread ----------
  if (!hasImage) {
    return (
      <section className="w-full overflow-hidden"
               style={{ background: "#EFE6D5" }}
               data-testid={`macro-render-${block.id}-${mode}`}>
        <div className={`mx-auto ${isMobile ? "px-6 py-14" : "max-w-[720px] px-6 py-24"} text-center`}>
          {m.eyebrow && (
            <div className="text-[10px] tracking-[0.32em] uppercase mb-4"
                 style={{ color: "#8A6A46" }}>{m.eyebrow}</div>
          )}
          {m.headline && (
            <div className="font-serif leading-[1.15]"
                 style={{
                   fontSize: isMobile ? "clamp(22px, 5.6vw, 28px)" : "clamp(28px, 2.6vw, 40px)",
                   color: "#2A1D14",
                 }}>{m.headline}</div>
          )}
          {m.thought && (
            <div className={`${m.eyebrow || m.headline ? "mt-6" : ""} font-serif italic leading-[1.6] mx-auto max-w-[52ch]`}
                 style={{
                   fontSize: isMobile ? "16px" : "clamp(17px, 1.4vw, 20px)",
                   color: "#3E2E20",
                 }}>{m.thought}</div>
          )}
          {m.signature && (
            <div className="mt-6 text-[11px] tracking-[0.24em] uppercase"
                 style={{ color: "#8A6A46" }}>— {m.signature}</div>
          )}
        </div>
      </section>
    );
  }

  // -------- With image → edge-to-edge dominant photograph ----------
  // The thought sits inside a restrained gradient area at the bottom so the
  // text remains readable without a heavy card frame or shadow.
  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`macro-render-${block.id}-${mode}`}>
      <div className="relative w-full">
        <div className={`w-full ${isMobile ? "aspect-[4/5]" : "aspect-[16/9]"} bg-neutral-200`}
             data-testid="macro-image-frame">
          <img
            src={activeImage.url}
            alt={activeImage.alt || m.headline || ""}
            className="w-full h-full object-cover block"
            style={{
              objectPosition: `${fx}% ${fy}%`,
              transform: `scale(${activeImage.zoom || 1})`,
              transformOrigin: `${fx}% ${fy}%`,
            }} />
        </div>
        {(Eyebrow || Headline || Thought || Signature) && (
          <div className="absolute inset-x-0 bottom-0 pointer-events-none"
               style={{
                 background: "linear-gradient(to top, rgba(20,12,6,0.72) 0%, rgba(20,12,6,0.45) 55%, rgba(20,12,6,0) 100%)",
               }}>
            <div className={`mx-auto ${isMobile ? "px-6 py-8" : "max-w-[1200px] px-10 py-12"}`}>
              {Eyebrow}
              {Headline}
              {Thought}
              {Signature}
            </div>
          </div>
        )}
      </div>
      {Caption && (
        <div className={`mx-auto ${isMobile ? "px-6 py-4" : "max-w-[1200px] px-10 py-4"}`}>
          {Caption}
        </div>
      )}
    </section>
  );
}
